import {
  Component,
  computed,
  effect,
  inject,
  input,
  linkedSignal,
  output,
  signal,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import {
  DatepickerComponent,
  InputModule,
  SelectSearchComponent,
  SelectComponent,
} from '@beautyapp/form';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzSpaceModule } from 'ng-zorro-antd/space';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzTypographyModule } from 'ng-zorro-antd/typography';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  AddUserComponent,
  UserVerifyComponent,
  VisitAddFacade,
} from '@beautyapp/shared';
import { NzTooltipDirective, NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  catchError,
  EMPTY,
  lastValueFrom,
  Observable,
  of,
  throwError,
} from 'rxjs';
import { defaultIfEmpty } from 'rxjs/operators';
import { NzDrawerRef, NzDrawerService } from 'ng-zorro-antd/drawer';
import { addMinutes, format } from 'date-fns';
import {
  canChangeEmployee as canChangeEmployeeRule,
  employeeOptions as employeeOptionsRule,
  probeWindow,
} from './visit-add-booking.helpers';
import {
  AvailabilityConflict,
  BookingService,
  BookingSourceService,
  EmployeeProcedureService,
  EmployeeService,
  NotificationService,
  ServiceService,
  timeStrToSeconds,
  User,
  UserService,
  salaryForRequest,
  isMasked,
} from '@beautyapp/core';
import { map, switchMap, tap } from 'rxjs/operators';
import { NgxPermissionsModule, NgxPermissionsService } from 'ngx-permissions';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { AddBookingComponent } from '../add-booking/add-booking.component';
import { NzModalService } from 'ng-zorro-antd/modal';
import { NzDividerComponent } from 'ng-zorro-antd/divider';
import { NzPopconfirmDirective } from 'ng-zorro-antd/popconfirm';
import { EditBookingInfoComponent } from '../edit-booking-info/edit-booking-info.component';
import { AddBookingServiceComponent } from '../add-booking-service/add-booking-service.component';
import { UserCardComponent } from '../user-card/user-card.component';
import { rxResource } from '@angular/core/rxjs-interop';
import { UserViewComponent } from '@beautyapp/users';
import { createDateRangeValidator } from '../../../validators/appointment.validator';
import { EventInput } from '@fullcalendar/core';

import { missingVisitFieldKey } from './missing-visit-field';

/** Wall-clock shape the pending-overlap check compares against. */
type PendingOverlapCandidate = {
  employeeId: string;
  date: string;
  start: string;
  end: string;
};

@UntilDestroy()
@Component({
  selector: 'beautyapp-visit-add-booking',
  imports: [
    InputModule,
    DatepickerComponent,
    SelectComponent,
    UserCardComponent,
    NzButtonModule,
    NzCardModule,
    NzCheckboxModule,
    NzGridModule,
    NzIconModule,
    NzInputModule,
    NzSpaceModule,
    NzTagModule,
    NzTypographyModule,
    ReactiveFormsModule,
    TranslatePipe,
    FormsModule,
    NzToolTipModule,
    NgxPermissionsModule,
    NzEmptyModule,
    NzDividerComponent,
    NzPopconfirmDirective,
    DatePipe,
    NzTooltipDirective,
  ],
  templateUrl: './visit-add-booking.component.html',
  styleUrl: './visit-add-booking.component.scss',
})
export class VisitAddBookingComponent {
  visitAddFacade = inject(VisitAddFacade);
  modalService = inject(NzModalService);
  drawerService = inject(NzDrawerService);
  drawerRef = inject(NzDrawerRef);
  translateService = inject(TranslateService);
  serviceService = inject(ServiceService);
  employeeService = inject(EmployeeService);
  bookingService = inject(BookingService);
  bookingSourceService = inject(BookingSourceService);
  permissionsService = inject(NgxPermissionsService);
  employeeServiceService = inject(EmployeeProcedureService);
  notificationService = inject(NotificationService);
  userService = inject(UserService);

  form: FormGroup = new FormGroup(
    {
      onUserRequest: new FormControl(false),
      userId: new FormControl(null, Validators.required),
      sourceId: new FormControl(null),
      bookingDate: new FormControl(null, Validators.required),
      startTime: new FormControl(null, Validators.required),
      startTimeDate: new FormControl(null, Validators.required),
      endTime: new FormControl(null, Validators.required),
      endTimeDate: new FormControl(null, Validators.required),
      sendNotification: new FormControl(true),
      description: new FormControl(null),
    },
    {
      validators: [createDateRangeValidator()],
    }
  );

  services$: Observable<any> = this.serviceService.dropdown();

  bookingDate = input<Date>();
  startTime = input<Date>();
  endTime = input<Date>();
  employeeId = input<string | null>();
  roomId = input<string>();
  // Bound reference to DayGridComponent.findOverlappingPendingOnline,
  // threaded down via NZ_DRAWER_DATA (day-grid -> VisitManageComponent ->
  // here). Reads the calendar's live pending-online list at call time, not
  // a snapshot from when the slot was clicked (booki-168).
  checkPendingOverlap = input<
    ((candidate: PendingOverlapCandidate) => EventInput | null) | undefined
  >();

  getVisit = output<number>();

  /** Flag to determine if service selection is needed */
  needChooseService = signal(false);

  /** Currently selected service */
  service = signal<any | null>(null);
  selectedUser = signal<any | null>(null);

  /** Products associated with the service */
  serviceProducts = signal<any[]>([]);

  /** Special services associated with the booking */
  serviceSpecs = signal<any[]>([]);

  loading = signal(false);
  bookings = signal<any[]>([]);

  totalPrice = computed(() => {
    return +(
      this.bookings()?.reduce((acc, b) => {
        return b.totalPrice + acc;
      }, 0) || 0
    ).toFixed(2);
  });

  totalExpense = computed(() => {
    return +(
      this.bookings()?.reduce((acc, b) => {
        return acc + +b.totalExpense;
      }, 0) || 0
    ).toFixed(2);
  });

  totalMinutes = computed(() => {
    console.log('totalMinutes', this.bookings());
    return this.bookings()?.reduce((acc, b) => {
      return acc + +b.totalMinutes;
    }, 0);
  });

  /**
   * beaut-545. Who the visit is for. Seeded from the calendar column the
   * operator clicked, then owned by the header select.
   *
   * A linkedSignal rather than a plain signal so a re-opened drawer with a
   * different employeeId still re-seeds; a plain signal would keep the first
   * one forever.
   */
  selectedEmployeeId = linkedSignal(() => this.employeeId() ?? null);

  /**
   * beaut-545. The employee is fixed once the visit holds a procedure: one
   * booking carries exactly one employee, and this page creates one booking.
   * Swapping afterwards would silently disagree with the rows already listed
   * below, each of which was priced and time-slotted for the employee it was
   * added under.
   */
  canChangeEmployee = computed(() =>
    canChangeEmployeeRule({
      bookingCount: this.bookings().length,
      isCompleted: this.visitAddFacade.isCompleted,
    })
  );

  employeeResource = rxResource({
    params: () => ({ employeeId: this.selectedEmployeeId() }),
    stream: ({ params }) => {
      if (!params.employeeId) return of(null);
      return this.employeeService
        .getById(params.employeeId)
        .pipe(catchError(() => of(null)));
    },
  });

  bookingSourcesResource = rxResource({
    stream: () =>
      this.bookingSourceService.getAll().pipe(
        defaultIfEmpty([]),
        catchError(() => of([]))
      ),
  });

  bookingSources = computed(() => this.bookingSourcesResource.value() || []);

  employee = computed(() => this.employeeResource.value());

  /**
   * beaut-545. The day the page is currently on, as yyyy-MM-dd.
   *
   * The date lives in a reactive form, not a signal, so it is mirrored here:
   * both the employee list and the busy probe have to follow it when the
   * operator moves the visit to another day.
   */
  bookingDateValue = signal<string | null>(null);

  /**
   * beaut-545. Who is actually working that day — the same list the calendar
   * builds its columns from (scheduled, in-calendar, this branch). Offering
   * the whole staff dropdown here invites booking someone who is off.
   */
  dayEmployeesResource = rxResource({
    params: () => ({ date: this.bookingDateValue() }),
    stream: ({ params }) => {
      if (!params.date) return of([]);
      return this.employeeService
        .getEmployeeToCalendar(params.date)
        .pipe(catchError(() => of([])));
    },
  });

  dayEmployees = computed(() => this.dayEmployeesResource.value() || []);

  employeeOptions = computed(() => {
    const current = this.employee();
    return employeeOptionsRule({
      workingToday: this.dayEmployees(),
      current: current
        ? { id: current.id, fullName: current.user?.fullName || '' }
        : null,
    });
  });

  /** beaut-545. What the last busy probe found. Empty means nothing to say. */
  employeeBusyConflicts = signal<AvailabilityConflict[]>([]);
  employeeBusy = computed(() => this.employeeBusyConflicts().length > 0);

  get isAnonymous() {
    return this.form.get('isAnonymous')?.value;
  }

  /**
   * Gets the sale price from the form
   */
  get salePrice() {
    return this.form.get('salePrice')?.value;
  }

  constructor() {
    effect(() => {
      if (this.bookingDate()) {
        this.bookingDateValue.set(format(this.bookingDate()!, 'yyyy-MM-dd'));
        this.form
          .get('bookingDate')
          ?.setValue(format(this.bookingDate()!, 'yyyy-MM-dd'));
        this.form
          .get('startTime')
          ?.setValue(format(this.bookingDate()!, 'HH:mm'));
        this.form
          .get('startTimeDate')
          ?.setValue(format(this.bookingDate()!, 'yyyy-MM-dd HH:mm'));
      }
    });

    // beaut-545. The datepicker writes to the form, not to a signal, so the
    // mirror has to be kept by hand. A stale date would list the wrong day's
    // employees and leave a "busy" badge standing for a day nobody checked.
    this.form
      .get('bookingDate')
      ?.valueChanges.pipe(untilDestroyed(this))
      .subscribe((value: any) => {
        if (!value) return;
        const date =
          typeof value === 'string' ? value : format(value, 'yyyy-MM-dd');
        if (date === this.bookingDateValue()) return;
        this.bookingDateValue.set(date);
        void this.checkEmployeeBusy();
      });

    effect(() => {
      const employee = this.employee();
      if (!employee) return;

      // beaut-572. NEVER patch a masked value into the form.
      //
      // `fixedSalary` and `outputPercent` carry
      // `@RequirePermission('field.employee_salary')` on the server, so a
      // branch that revokes salary visibility sends `'***'` instead of a
      // number. Patched in verbatim, that string was posted back to
      // `POST /booking/visitBooking` and the operator got an
      // `Http failure response` — the defect the card reports.
      //
      // Left undefined rather than zeroed. Zero is a real salary, and sending
      // it would tell the server "this procedure pays nothing" — which is how
      // a booking ends up with percent 0 AND fixed 0 and the visit shows
      // "პროცედურაში არ არის მითითებული ხელფასის პროცენტი ან ფიქსირებული".
      // Absent means "I could not see it"; the server then resolves the
      // employee's own default, which is what should have been saved all along.
      this.form.patchValue({
        employeeId: employee.id,
        salaryFixed: isMasked(employee.fixedSalary) ? undefined : employee.fixedSalary,
        salaryPercent: isMasked(employee.outputPercent) ? undefined : employee.outputPercent,
        salaryPercentFromPrice: false,
      });
    });
  }

  getServiceById(serviceId: any) {
    this.serviceService
      .getById(serviceId)
      .pipe(
        untilDestroyed(this),
        map(async (res: any) => {
          const timeToSeconds = timeStrToSeconds(res.time);
          const endTime = format(
            addMinutes(
              new Date(this.form.get('startTime')?.value),
              timeToSeconds
            ),
            'yyyy-MM-dd HH:mm'
          );
          console.log(endTime);
          this.form.patchValue({
            service: res,
            endTime: endTime,
          });
          this.service.set(res);
          await this.chooseService(res);
          return res;
        })
      )
      .subscribe();
  }

  addVisit(): void {
    if (this.loading()) return;

    this.form.markAllAsTouched();

    // beaut-558: say which field is missing. This used to return in silence,
    // so Save did nothing and named nothing — and the customer control the
    // operator sees is standalone, outside this form, so it cannot show the
    // error `userId` is carrying. The check just below has always spoken up;
    // this one now matches it.
    if (this.form.invalid) {
      this.notificationService.error(
        this.translateService.instant(missingVisitFieldKey(this.form)),
      );
      return;
    }

    if (this.bookings().length === 0) {
      this.notificationService.error(
        this.translateService.instant('visit.bookingRequired')
      );
      return;
    }

    // Submit-time only (booki-168): staff can still change the time inside
    // this form, so checking any earlier (e.g. on the slot click that opened
    // it) could warn on a time that's since changed, or miss one that
    // hasn't. This is the last point before the booking is actually sent.
    this.submitAfterPendingOverlapCheck();
  }

  /**
   * Advisory only — staff must be able to keep the override, so this asks
   * rather than blocks (unlike a live slot hold, which the backend rejects
   * outright). See DayGridComponent.findOverlappingPendingOnline for the
   * full rationale.
   *
   * Finding the overlap AND showing its confirm modal both sit inside one
   * try/catch: a failure anywhere in this path — a broken `checkPendingOverlap`
   * callback, or `modalService.confirm` itself throwing — falls straight
   * through to `submitVisit()` below. An advisory check must never be the
   * reason a booking doesn't go through.
   */
  private submitAfterPendingOverlapCheck(): void {
    try {
      const overlap = this.findPendingOnlineOverlap();
      if (overlap) {
        this.confirmPendingOnlineOverlap(overlap, () => this.submitVisit());
        return;
      }
    } catch {
      // Fall through to submitVisit() below.
    }
    this.submitVisit();
  }

  private findPendingOnlineOverlap(): EventInput | null {
    const check = this.checkPendingOverlap();
    if (!check) return null;
    for (const booking of this.bookings()) {
      const match = check({
        employeeId: booking.employeeId,
        date: booking.bookingDate,
        start: booking.startTime,
        end: booking.endTime,
      });
      if (match) return match;
    }
    return null;
  }

  private confirmPendingOnlineOverlap(
    overlap: EventInput,
    proceed: () => void
  ): void {
    const props = (overlap.extendedProps ?? {}) as {
      fullName?: string;
      pendingStart?: string;
      pendingEnd?: string;
      paidAmount?: number | null;
      originalAmount?: number | null;
    };
    this.modalService.confirm({
      nzTitle: this.translateService.instant(
        'calendar.pendingOnline.overlapWarningTitle'
      ),
      nzContent: this.translateService.instant(
        'calendar.pendingOnline.overlapWarningContent',
        {
          name:
            props.fullName ||
            this.translateService.instant(
              'calendar.pendingOnline.overlapWarningUnknownCustomer'
            ),
          start: props.pendingStart ?? '',
          end: props.pendingEnd ?? '',
          paid: props.paidAmount ?? 0,
          total: props.originalAmount ?? 0,
        }
      ),
      nzOkText: this.translateService.instant(
        'calendar.pendingOnline.overlapWarningConfirm'
      ),
      nzCancelText: this.translateService.instant('buttons.cancel'),
      nzOnOk: () => proceed(),
    });
  }

  private submitVisit(): void {
    this.loading.set(true);

    const {
      onUserRequest,
      userId,
      sourceId,
      bookingDate,
      startTime,
      startTimeDate,
      endTime,
      endTimeDate,
      sendNotification,
      description,
    } = this.form.value;
    // console.log('form value', this.form.value);
    // console.log('visitId', this.visitId);
    console.log('this.bookings()', this.bookings());
    this.bookingService
      .createVisit({
        onUserRequest,
        userId,
        sourceId,
        bookingDate,
        startTime,
        startTimeDate,
        endTime,
        endTimeDate,
        sendNotification,
        description,
        bookings: this.bookings().map((booking: any) => ({
          employeeId: booking.employeeId,
          roomId: booking.roomId,
          bookingDate: booking.bookingDate,
          startTime: booking.startTime,
          startTimeDate: booking.startTimeDate,
          endTime: booking.endTime,
          endTimeDate: booking.endTimeDate,
          sendNotification: booking.sendNotification,
          description: booking.description,
          bookingServices: booking.bookingServices.map((bs: any) => ({
            serviceId: bs.serviceId,
            employeeId: booking.employeeId,
            price: +bs.price || 0,
            salePrice: bs.salePrice ? +bs.salePrice : null,
            time: bs.time,
            // beaut-572. `|| 0` destroyed the one signal the server needs.
            //
            // A masked value coerces to NaN and then to 0, and a blank control
            // did the same, so the request always claimed "this procedure pays
            // nothing" — which the server has no way to tell apart from a
            // deliberate zero. Sent as `undefined` the key is dropped from the
            // JSON altogether, and the server substitutes the employee's own
            // default. A real 0 the operator typed still goes as 0.
            salaryFixed: salaryForRequest(bs.salaryFixed),
            salaryPercent: salaryForRequest(bs.salaryPercent),
            salaryPercentFromPrice: bs.salaryPercentFromPrice,
            expence: +bs.expence || 0,
            // Additional cost — sent unchanged, never recalculated (beaut-240).
            additionalExpence: +bs.additionalExpence || 0,
            offerPurchaseId: bs.offerPurchaseId || null,
            offerServiceId: bs.offerServiceId || null,
            packagePurchaseId: bs.packagePurchaseId || null,
            packageItemId: bs.packageItemId || null,
            additionalEmployees: bs.additionalEmployees,
            serviceProducts:
              bs.serviceProducts?.map((sp: any) => ({
                productId: sp.productId,
                extra: sp.extra,
                grSum: sp.grSum,
                priceValue: +sp.priceValue || 0,
                quantitySum: +sp.quantitySum || 0,
              })) || [],
            specServices:
              bs.specServices?.map((ss: any) => ({
                serviceId: ss.serviceId,
                employeeId: ss.employeeId,
                price: +ss.price || 0,
                // Fixed cost carried from the special procedure (beaut-257).
                expence: +ss.expence || 0,
                // beaut-609: same rule as the procedure above — an empty or
                // masked salary is left out, not sent as 0, so the server can
                // apply the employee's default.
                salaryFixed: salaryForRequest(ss.salaryFixed),
                salaryPercent: salaryForRequest(ss.salaryPercent),
              })) || [],
          })),
        })),
      })
      .pipe(
        untilDestroyed(this),
        catchError((error) => {
          this.loading.set(false);
          return throwError(() => error);
        })
      )
      .subscribe((res: any) => {
        this.getVisit.emit(res.id);
      });
  }

  async chooseService(service: any) {
    if (!service) {
      console.error('Invalid service provided to chooseService');
      return;
    }

    if (this.employee()) {
      const employeeService = await lastValueFrom(
        this.employeeServiceService.getByEmployeeAndService(
          this.employee().id,
          service.id
        )
      );

      this.needChooseService.set(false);

      // beaut-572. `employee_service.fixedSalary` / `.outputPercent` carry the
      // same `@RequirePermission('field.employee_salary')` as the employee's
      // own, so both of these can be `'***'` too. Left undefined rather than
      // zeroed — see the note on the serializer.
      if (employeeService) {
        this.form.patchValue({
          salaryFixed: isMasked(employeeService.fixedSalary)
            ? undefined
            : employeeService.fixedSalary,
          salaryPercent: isMasked(employeeService.outputPercent)
            ? undefined
            : employeeService.outputPercent,
          salaryPercentFromPrice: false,
        });
      } else {
        const outputPercent = this.visitAddFacade.employee()?.outputPercent;
        this.form.patchValue({
          salaryFixed: null,
          salaryPercent: isMasked(outputPercent) ? undefined : outputPercent,
          salaryPercentFromPrice: false,
        });
      }
    }
    this.service.set(service);

    this.form.patchValue({
      time: service.time,
      price: service.price,
      salePrice: +service.discountPrice,
    });

    const startTime = this.form.get('startTime')?.value;

    // Validate startTime and service.time before calling setEndTime
    if (
      startTime &&
      startTime instanceof Date &&
      !isNaN(startTime.getTime()) &&
      service.time &&
      typeof service.time === 'string' &&
      (service.time as string).trim() !== ''
    ) {
      try {
        // Validate time format
        const timeInMinutes = timeStrToSeconds(service.time);
        if (timeInMinutes > 0) {
          this.setEndTime(startTime, service.time);
        } else {
          console.error(
            'Invalid service time duration (zero or negative minutes):',
            timeInMinutes
          );
        }
      } catch (e) {
        console.error('Error processing service time:', e);
      }
    }

    this.serviceProducts.set(
      this.mapServiceProducts(service.serviceProducts || [])
    );

    if (service.autoFix && +service.fixSum === 0) {
      this.form.patchValue({
        expense: this.calculateTotalExpense(),
      });
    } else if (service.autoFix && +service.fixSum > 0) {
      this.form.patchValue({
        expense: service.fixSum,
      });
    }
  }

  /**
   * Calculates and sets the end time based on start time and duration
   * @param startTime The starting time
   * @param time The duration in format "HH:MM"
   */
  setEndTime(startTime: Date, time: string) {
    try {
      // Validate inputs
      if (!startTime) {
        console.error('Invalid startTime provided to setEndTime:', startTime);
        return; // Don't update if startTime is invalid
      }

      if (!time) {
        console.error('Invalid time duration provided to setEndTime');
        return; // Don't update if time is invalid
      }

      const timeInMinutes = timeStrToSeconds(time);

      if (timeInMinutes <= 0) {
        console.error(
          'Invalid time duration (zero or negative minutes):',
          timeInMinutes
        );
        return; // Don't update if duration is invalid
      }

      // Create a new Date object to avoid modifying the original
      const startTimeDate = new Date(startTime);

      // Calculate end time
      const endTime = new Date(addMinutes(startTimeDate, timeInMinutes));

      // Validate the result
      if (!endTime) {
        console.error('Invalid endTime calculated:', endTime);
        return; // Don't update if result is invalid
      }

      // Update the form
      this.form.patchValue({
        endTime: format(endTime, 'HH:mm'),
        endTimeDate: new Date(endTime),
      });
    } catch (e) {
      console.error('Error in setEndTime:', e);
      // Don't update the form on error
    }
  }

  /**
   * Maps service products to include calculated price
   */
  private mapServiceProducts(products: any[]): any[] {
    return (
      products?.map((m: any) => {
        let price = 0;
        if (m.product) {
          if (m.quantitySum > 0) {
            price = +(m.product.salePricePerItem || 0) * +m.quantitySum;
          }
          if (m.grSum > 0) {
            price = +(
              (+(m.product.salePricePerItem || 0) / +m.product.grPerItem) *
              +m.grSum
            ).toFixed(2);
          }
        } else {
          price = +m.priceValue || 0;
        }
        return {
          ...m,
          productId: m.product?.id || m.productId,
          productName: m.product?.name || m.productName || null,
          priceValue: price,
          price,
        };
      }) || []
    );
  }

  /**
   * Calculates total expense from service products
   */
  private calculateTotalExpense(): number {
    return this.serviceProducts().reduce((previousValue, currentValue) => {
      return previousValue + +currentValue.price;
    }, 0);
  }

  /**
   * beaut-545. Called by the header select. Everything downstream keys off
   * `selectedEmployeeId` — the employee lookup, the salary prefill, and the
   * data handed to the add-booking panel — so this one write is the whole
   * change.
   */
  setEmployee(employeeId: string | null) {
    this.selectedEmployeeId.set(employeeId ?? null);
    void this.checkEmployeeBusy();
  }

  /**
   * beaut-545. Is the chosen employee already taken at this time?
   *
   * Asked on every swap, and again when the day changes, because a badge that
   * outlives the thing it described is worse than no badge. Nothing is blocked
   * on the answer: the operator may well be double-booking on purpose, and the
   * real check runs in the booking panel on save.
   */
  async checkEmployeeBusy(): Promise<void> {
    const employeeId = this.selectedEmployeeId();
    const date = this.bookingDateValue();
    const window = probeWindow(this.form.get('startTime')?.value);

    if (!employeeId || !date || !window) {
      this.employeeBusyConflicts.set([]);
      return;
    }

    const response = await lastValueFrom(
      this.bookingService
        .checkEmployeeAvailability({
          employeeId,
          date,
          startTime: window.startTime,
          endTime: window.endTime,
        })
        .pipe(
          catchError(() => of({ available: true, conflicts: [] }))
        )
    );

    this.employeeBusyConflicts.set(
      response.available ? [] : response.conflicts || []
    );
  }

  addBooking() {
    const getLatestEndTime = this.form.get('startTimeDate')?.value;
    const bookingDate = this.form.get('bookingDate')?.value;
    console.log('bookingDate', bookingDate);
    this.modalService
      .create({
        nzClosable: true,
        nzMaskClosable: false,
        nzTitle: this.translateService.instant('calendar.planVisit'),
        nzContent: AddBookingComponent,
        nzWidth: window.innerWidth <= 1024 ? '100%' : '70%',
        nzFooter: null,
        nzData: {
          roomId: this.roomId(),
          employee: this.employee(),
          employeeId: this.selectedEmployeeId(),
          // A procedure after the first is its own booking with its own
          // employee: open it pre-selected but changeable (see
          // isEmployeePickerLocked).
          followUpBooking: this.bookings().length > 0,
          userId: this.visitAddFacade.user()?.id,
          bookingDate: format(bookingDate, 'yyyy-MM-dd'),
          startTime: format(getLatestEndTime, 'yyyy-MM-dd HH:mm'),
        },
      })
      .afterClose.pipe(untilDestroyed(this))
      .subscribe((res) => {
        console.log('res', res);
        if (res) {
          this.form.patchValue({
            startTime: format(res.startTimeDate, 'HH:mm'),
            startTimeDate: new Date(res.startTimeDate),
            endTime: format(res.endTimeDate, 'HH:mm'),
            endTimeDate: new Date(res.endTimeDate),
          });
          this.bookings.update((current) => [
            ...current,
            {
              ...res,
              totalPrice: +res.bookingServices
                .reduce((acc: number, bs: any) => {
                  if (bs.packagePurchaseId) return acc;
                  if (bs.salePrice && +bs.salePrice > 0) {
                    return acc + +bs.salePrice;
                  }
                  return acc + +bs.price;
                }, 0)
                .toFixed(2),
              totalExpense: +res.bookingServices
                .reduce((acc: number, bs: any) => {
                  // Expense columns already hold product + fixed cost when
                  // autoFix seeded them — do not re-add recipe priceValue
                  // (that invented "დანახარჯი" on procedures with expence=0).
                  return (
                    acc +
                    +bs.expence +
                    (+bs.additionalExpence || 0) +
                    (bs.specServices?.reduce(
                      (sAcc: number, ss: any) => sAcc + (+ss.expence || 0),
                      0
                    ) || 0)
                  );
                }, 0)
                .toFixed(2),
              totalMinutes: res.bookingServices.reduce(
                (acc: number, bs: any) => acc + timeStrToSeconds(bs.time),
                0
              ),
            },
          ]);
        }
      });
  }

  deleteBookingService(
    bookingService: any,
    bookingIndex: number,
    indx: number
  ) {
    const findBooking = this.bookings().find((bs: any, index: number) => {
      return index === bookingIndex;
    });

    const bookingServices = findBooking.bookingServices.filter(
      (bs: any, index: number) => indx !== index
    );
    const timeToSecond = bookingServices.reduce(
      (acc: number, bs: any) => acc + timeStrToSeconds(bs.time),
      0
    );
    this.bookings.update((current) => {
      const next = [...current];
      next[bookingIndex] = {
        ...findBooking,
        startTimeDate: new Date(findBooking.startTimeDate),
        startTime: format(findBooking.startTimeDate, 'HH:mm'),
        endTimeDate: new Date(
          addMinutes(new Date(findBooking.startTimeDate), timeToSecond)
        ),
        endTime: format(
          addMinutes(new Date(findBooking.startTimeDate), timeToSecond),
          'HH:mm'
        ),
        totalPrice: +bookingServices
          .reduce((acc: number, bs: any) => {
            if (bs.packagePurchaseId) return acc;
            if (bs.salePrice && +bs.salePrice > 0) {
              return acc + +bs.salePrice;
            }
            return acc + +bs.price;
          }, 0)
          .toFixed(2),
        totalExpense: +bookingServices
          .reduce((acc: number, bs: any) => {
            return (
              acc +
              +bs.expence +
              (+bs.additionalExpence || 0) +
              (bs.specServices?.reduce(
                (sAcc: number, ss: any) => sAcc + (+ss.expence || 0),
                0
              ) || 0)
            );
          }, 0)
          .toFixed(2),
        totalMinutes: bookingServices.reduce(
          (acc: number, bs: any) => acc + timeStrToSeconds(bs.time),
          0
        ),
        bookingServices: bookingServices,
      };
      return next;
    });
  }

  addBookingService(booking: any, index: number) {
    console.log('booking', booking);
    this.modalService
      .create({
        nzClosable: true,
        nzMaskClosable: false,
        nzTitle: this.translateService.instant('calendar.planVisit'),
        nzContent: AddBookingServiceComponent,
        nzWidth: window.innerWidth <= 1024 ? '100%' : '70%',
        nzFooter: null,
        nzData: {
          outputMode: true,
          booking,
          bookingDate: this.visitAddFacade.bookingDate,
          userId: this.visitAddFacade.user()?.id,
          viewOnly: this.visitAddFacade.isCompleted,
        },
      })
      .afterClose.pipe(untilDestroyed(this))
      .subscribe((res) => {
        if (res) {
          const updateBooking = {
            ...booking,
            bookingServices: [...booking.bookingServices, res],
          };

          const timeToSecond = updateBooking.bookingServices.reduce(
            (acc: number, bs: any) => acc + timeStrToSeconds(bs.time),
            0
          );
          this.bookings.update((current) => {
            const next = [...current];
            next[index] = {
              ...updateBooking,
              startTimeDate: new Date(updateBooking.startTimeDate),
              startTime: format(updateBooking.startTimeDate, 'HH:mm'),
              endTimeDate: new Date(
                addMinutes(new Date(updateBooking.startTimeDate), timeToSecond)
              ),
              endTime: format(
                addMinutes(new Date(updateBooking.startTimeDate), timeToSecond),
                'HH:mm'
              ),
              totalPrice: +updateBooking.bookingServices
                .reduce((acc: number, bs: any) => {
                  if (bs.packagePurchaseId) return acc;
                  if (bs.salePrice && +bs.salePrice > 0) {
                    return acc + +bs.salePrice;
                  }
                  return acc + +bs.price;
                }, 0)
                .toFixed(2),
              totalExpense: +updateBooking.bookingServices
                .reduce((acc: number, bs: any) => {
                  return (
                    acc +
                    +bs.expence +
                    (+bs.additionalExpence || 0) +
                    (bs.specServices?.reduce(
                      (sAcc: number, ss: any) => sAcc + (+ss.expence || 0),
                      0
                    ) || 0)
                  );
                }, 0)
                .toFixed(2),
              totalMinutes: updateBooking.bookingServices.reduce(
                (acc: number, bs: any) => acc + timeStrToSeconds(bs.time),
                0
              ),
            };
            return next;
          });
        }
      });
  }

  editBookingService(bookingService: any, bookingIndex: number, indx: number) {
    console.log('bookingService', bookingService);
    this.modalService
      .create({
        nzClosable: true,
        nzMaskClosable: false,
        nzTitle: this.translateService.instant('calendar.planVisit'),
        nzContent: AddBookingServiceComponent,
        nzWidth: window.innerWidth <= 1024 ? '100%' : '70%',
        nzFooter: null,
        nzData: {
          outputMode: true,
          employee: bookingService.employee,
          employeeId: bookingService.employeeId,
          bookingService,
          bookingDate: this.visitAddFacade.bookingDate,
          userId: this.visitAddFacade.user()?.id,
          viewOnly: this.visitAddFacade.isCompleted,
        },
      })
      .afterClose.pipe(untilDestroyed(this))
      .subscribe((res) => {
        console.log('res', res);
        if (res) {
          const startTime = this.form.get('startTimeDate')?.value;
          this.setEndTime(startTime, res.time);
          const findBooking = this.bookings().find((bs: any, index: number) => {
            return index === bookingIndex;
          });

          const timeToSecond = findBooking.bookingServices.reduce(
            (acc: number, bs: any) => acc + timeStrToSeconds(bs.time),
            0
          );

          const endTime = addMinutes(
            new Date(findBooking.startTimeDate),
            timeToSecond
          );

          const updateBooking = {
            ...findBooking,
            startTimeDate: new Date(findBooking.startTimeDate),
            startTime: format(findBooking.startTimeDate, 'HH:mm'),
            endTimeDate: new Date(endTime),
            endTime: format(endTime, 'HH:mm'),
            bookingServices: findBooking.bookingServices.map(
              (bs: any, index: number) => {
                if (index === indx) {
                  return {
                    ...bookingService,
                    ...res,
                  };
                }
                return bs;
              }
            ),
          };

          this.bookings.update((current) => {
            const next = [...current];
            next[bookingIndex] = {
              ...updateBooking,
              totalPrice: +updateBooking.bookingServices
                .reduce((acc: number, bs: any) => {
                  if (bs.packagePurchaseId) return acc;
                  if (bs.salePrice && +bs.salePrice > 0) {
                    return acc + +bs.salePrice;
                  }
                  return acc + +bs.price;
                }, 0)
                .toFixed(2),
              totalExpense: +updateBooking.bookingServices
                .reduce((acc: number, bs: any) => {
                  return (
                    acc +
                    +bs.expence +
                    (+bs.additionalExpence || 0) +
                    (bs.specServices?.reduce(
                      (sAcc: number, ss: any) => sAcc + (+ss.expence || 0),
                      0
                    ) || 0)
                  );
                }, 0)
                .toFixed(2),
              totalMinutes: updateBooking.bookingServices.reduce(
                (acc: number, bs: any) => acc + timeStrToSeconds(bs.time),
                0
              ),
            };
            return next;
          });
        }
      });
  }

  editBooking(booking: any, index: number) {
    this.modalService
      .create({
        nzClosable: true,
        nzMaskClosable: false,
        nzTitle: this.translateService.instant('calendar.planVisit'),
        nzContent: EditBookingInfoComponent,
        nzWidth: window.innerWidth <= 1024 ? '100%' : '70%',
        nzFooter: null,
        nzData: {
          employee: booking.employee,
          employeeId: booking.employeeId,
          userId: this.visitAddFacade.user()?.id,
          bookingDate: booking.bookingDate,
          startTime: booking.startTime,
          booking,
        },
      })
      .afterClose.pipe(untilDestroyed(this))
      .subscribe((res) => {
        this.setEndTime(res.startTimeDate, res.time);
        const bookingUpdated = {
          ...booking,
          ...res,
          startTimeDate: new Date(res.startTimeDate),
          startTime: format(res.startTimeDate, 'HH:mm'),
        };

        // update bookings immutably to trigger re-render
        this.bookings.update((current) => {
          const next = [...current];
          next[index] = bookingUpdated;
          return next;
        });
      });
  }

  deleteBooking(index: number) {
    this.bookings.update((current) => current.filter((_, i) => i !== index));
  }

  addNewUser() {
    const modal = this.modalService.create({
      nzTitle: this.translateService.instant('calendar.createUser'),
      nzContent: AddUserComponent,
      nzWidth: '900px',
      nzStyle: { top: '0px' },
    });

    modal.afterClose.subscribe((res) => {
      if (res) {
        this.visitAddFacade.searchCustomer(res.mobilePhone);
        this.visitAddFacade.selectCustomer(res);
        this.selectedUser.set(res);
        this.form.patchValue({
          userId: res.id,
        });
      }
    });
  }

  viewUser() {
    console.log(this.visitAddFacade.user());
    const user = this.visitAddFacade.user();
    let title = `${user.firstname} ${user.lastname || ''}`;
    if (user.cardNo) {
      title += ` - # ${user.cardNo}`;
    }
    const drawerRef = this.drawerService.create<
      UserViewComponent,
      { data: string },
      string
    >({
      nzTitle: title,
      nzContent: UserViewComponent,
      nzWrapClassName: 'user-drawer',
      nzBodyStyle: {
        padding: '0 15px 15px 15px',
      },
      nzData: {
        data: user.id,
      },
    });

    drawerRef.afterClose.pipe(untilDestroyed(this)).subscribe(() => {
      this.fetchFreshUser(user.id);
    });
  }

  verifyUser(user: User) {
    console.log(user);

    if (user.isVerified) {
      return;
    }

    this.modalService.create({
      nzTitle: this.translateService.instant('users.verifyUser'),
      nzContent: UserVerifyComponent,
      nzWidth: '400px',
      nzData: {
        userId: user.id,
      },
      nzOnOk: () => {
        this.fetchFreshUser(user.id);
      },
    });
  }

  fetchFreshUser(userId: string) {
    this.userService
      .getById(userId)
      .pipe(untilDestroyed(this))
      .subscribe((user) => {
        this.selectedUser.set(user);
        this.visitAddFacade.user.set(user);
      });
  }

  selectCustomer($event: any) {
    this.visitAddFacade.selectCustomer($event);
    this.visitAddFacade.userId = $event.id;
    this.visitAddFacade.user.set($event);
    this.form.patchValue({
      userId: $event.id,
    });
    this.selectedUser.set($event);
  }
}
